/**
 * LA CHAT DELL'INCONTRO — quando si mostra, come si legge, cosa si puo' mandare, e com'e' andato
 * l'invio. Logica pura: nessun `import.meta.env`, nessun orologio, `t` come parametro (come
 * `participantLabelOf`), cosi' la esercitano i banchi senza montare niente.
 *
 * 🔑 **PERCHE' ESISTE** (lotto T3 del fronte eFootball, decisione di collaudo della flotta del
 * 2026-09-26). eFootball e' un gioco SENZA integrazione: i due giocatori devono trovarsi nel gioco
 * — aggiungersi come amici con l'ID eFootball — e poi refertare a mano (ADR-0138). Fino a qui la
 * TMA non dava loro nessun canale. Il server la chat ce l'ha da G4 (2026-08-17); la TMA non la usava.
 * ⇒ Chat SI', solo sugli incontri in modo `manual`, dietro il flag beta della sezione (che e' gia'
 * tutta dietro `VITE_FEATURE_LIMULO_ENABLED` + ruolo `beta`: nessun flag nuovo).
 *
 * 🔴 **IL CONTRATTO E' LETTO NEL CODICE, NON MISURATO.** `qlash-limulo@origin/staging` = `7e4d86e`:
 * rotte in `app-qlash-api/backend/router.js:2484-2513` (tutte con `assertTournamentMatchAccess`,
 * nessun sanitizer ⇒ la forma grezza del motore e' il contratto), forme in
 * `app-qtournaments/app/core/chat.js`, stati dei rifiuti in `superficie/rotte/contratto.js:194-197`.
 * Nessuno di noi le ha ancora chiamate dal vivo: ogni lettura qui e' difensiva, e ogni «non lo so»
 * resta un «non lo so».
 */

import type {
  LimuloChatMessage,
  LimuloChatPage,
  LimuloChatParticipant,
  LimuloMatch,
  LimuloMatchChatRoom,
  LimuloPagination,
} from '../../types/limulo.ts';
import { limuloFailure, type LimuloFailure } from './errors.ts';
import { LIMULO_RESULT_MODE_MANUAL, participantLabelOf } from './match.ts';
import { normalizeInstant } from './model.ts';

// ── I numeri del server, tenuti qui a mano ────────────────────────────────────

/**
 * Il tetto del corpo: `MAX_CORPO_MESSAGGIO` (`core/chat.js:37`), contato **dopo il `trim`**
 * (`inviaMessaggio`), in unita' UTF-16 come `String.length` — le stesse del server, che e' JS.
 * Oltre, il motore rifiuta con `chat.corpo-troppo-lungo` (422).
 * ⚠️ E' un numero del SERVER copiato qui: governa il contatore e il bottone, e il giorno che
 * cambia di la' il peggio che succede e' un rifiuto nominato (422), che l'esito dell'invio dice.
 */
export const LIMULO_CHAT_MAX_CORPO = 2000;

/**
 * Da quanti caratteri si mostra il contatore. Sotto, un «37/2000» e' rumore su un messaggio che
 * dice «il mio ID e' 123-456»; vicino al tetto e' l'informazione che serve.
 */
export const LIMULO_CHAT_SOGLIA_CONTATORE = 1800;

/**
 * Quanti messaggi si chiedono: la pagina di default del motore (`PAGINA_CHAT_DEFAULT = 50`,
 * `core/chat.js:47`), dentro il suo tetto (`PAGINA_CHAT_MASSIMA = 100`; la capability rifiuta un
 * `limit` fuori da 1..100). Mandato esplicito e non lasciato al default: cosi' «ne mostro gli ultimi
 * 50» e' una cosa che il client SA, non una che il server potrebbe cambiare sotto di lui.
 */
export const LIMULO_CHAT_PAGINA = 50;

// ── Quando si mostra ─────────────────────────────────────────────────────────

/**
 * La chat si mostra su QUESTO incontro? Tre condizioni, tutte dichiarate dal server:
 *
 *  1. **`resultMode === 'manual'` DICHIARATO.** Il vuoto non vale: e' «il server non l'ha detto», e
 *     il default `manual` lo applica il MOTORE, non noi (v. `LIMULO_RESULT_MODE_MANUAL` in
 *     `match.ts`). Un incontro `provider` si gioca col risultato che arriva dal gioco: li' la chat
 *     non serve a trovarsi, e la decisione di collaudo la limita ai manuali.
 *  2. **Uno dei due lati e' `isMe === true`.** La stanza la legge e la scrive solo chi e' membro di
 *     una delle due parti (`puoLeggereLaStanza`, `core/chat.js:157`): a uno spettatore la rotta
 *     risponde 403, e mostrargli una chat che rifiuta sarebbe un invito a un errore certo.
 *     ⚠️ `isMe` ASSENTE non e' `false`, ma qui il verso del ripiego e' il silenzio: senza la prova
 *     che l'incontro e' mio, non si offre un canale che scrive.
 *  3. **Non e' un bye, e l'avversario c'e'.** Un turno passato, o un incontro non ancora composto
 *     (`players.b === null`, stato `incomplete`), non ha nessuno con cui trovarsi: la stanza
 *     conterrebbe solo la mia parte (revisione di #412, punto 3).
 */
export function chatVisibile(match: LimuloMatch): boolean {
  if (match.isBye) return false;
  if (!match.players.a || !match.players.b) return false;
  if (match.resultMode !== LIMULO_RESULT_MODE_MANUAL) return false;
  return match.players.a?.isMe === true || match.players.b?.isMe === true;
}

// ── Il bordo: forme grezze → tipi ─────────────────────────────────────────────

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Un messaggio da `messaggioSerializzato` (`core/chat.js:73-88`). */
export function messaggioChatOf(raw: unknown): LimuloChatMessage {
  const m = asRecord(raw);
  return {
    // `id` e `messageId` coincidono per costruzione del motore; si prende il primo che c'e'.
    id: asString(m.id) || asString(m.messageId),
    body: asString(m.body),
    messageType: asString(m.messageType),
    status: asString(m.status),
    senderId: asString(m.senderId).trim(),
    attachmentsCount: Array.isArray(m.attachments) ? m.attachments.length : 0,
    createdAt: asString(m.createdAt) || null,
  };
}

/**
 * Chi c'e' nella stanza. ⚠️ Una riga senza `userId` si scarta: senza id non si puo' riconoscere
 * come autore di niente, e tenerla darebbe un nome a nessun messaggio.
 */
function partecipantiOf(raw: unknown): LimuloChatParticipant[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((riga) => {
    const p = asRecord(riga);
    const userId = asString(p.userId).trim();
    if (userId === '') return [];
    return [{ userId, displayName: asString(p.displayName), username: asString(p.username) }];
  });
}

/**
 * La stanza da `GET …/chat` (`stanzaDiMatch`), o `null` se la risposta non e' una stanza.
 *
 * 🔴 **`participants` ASSENTE ⇒ `null`, non una stanza vuota.** Una stanza con `participants: []`
 * direbbe «qui non c'e' nessuno», e ogni autore diventerebbe STAFF — l'affermazione piu' forte che
 * questa schermata possa fare su un nome, ricavata da un campo che manca.
 */
export function stanzaChatOf(raw: unknown): LimuloMatchChatRoom | null {
  const s = asRecord(raw);
  if (!Array.isArray(s.participants)) return null;
  return {
    roomId: asString(s.roomId) || asString(s.id),
    participants: partecipantiOf(s.participants),
    lastMessageAt: asString(s.lastMessageAt) || null,
  };
}

/**
 * La pagina da `GET …/messages`: `data = {room, items, page, limit, total}` (`listaMessaggi`) e la
 * `pagination` FUORI da `data`, che il BFF aggiunge perche' `items` e' un array
 * (`sendCapabilityDataOk`, `router.js:603-611`).
 *
 * ⚠️ **`total` dal motore prima che dalla busta**: `buildPagination` lo ricopia, ma riempie `limit`
 * con 50 quando manca — un numero suo, non del motore. Il motore dichiara tutti e tre (la forma
 * `busta` del catalogo li pretende numerici), quindi la busta resta il ripiego.
 * ⛔ **`room` NON si legge da qui**: questa rotta non passa da `withPublicParticipantNames` per la
 * stanza (il bordo visita `items`, non `room`), quindi i nomi che porta sono quelli GREZZI della
 * cache del motore — non il nome pubblico di ADR-0125. I nomi vengono SOLO da `GET …/chat`.
 */
export function paginaChatOf(dati: unknown, busta: LimuloPagination | null): LimuloChatPage {
  const d = asRecord(dati);
  const items = Array.isArray(d.items) ? d.items.map(messaggioChatOf) : [];
  return {
    items,
    page: asFiniteNumber(d.page) ?? busta?.page ?? 1,
    limit: asFiniteNumber(d.limit) ?? busta?.limit ?? null,
    total: asFiniteNumber(d.total) ?? busta?.total ?? null,
  };
}

/**
 * Il messaggio scritto, dalla risposta del `POST` (201).
 *
 * 🔴 **La risposta NON e' il messaggio: e' `{room, message}`** (`inviaMessaggio`, `core/chat.js:
 * 277-280`), e il bordo la manda cosi' com'e'. Si legge `message`; il ripiego sul dato intero
 * serve solo se un giorno la busta si appiattisse, e si accetta solo se ha la faccia di un messaggio
 * (un `id`), mai «qualcosa e' tornato, quindi e' il messaggio».
 */
export function messaggioInviatoOf(dati: unknown): LimuloChatMessage | null {
  const d = asRecord(dati);
  const grezzo = d.message !== undefined ? d.message : d;
  const messaggio = messaggioChatOf(grezzo);
  return messaggio.id === '' ? null : messaggio;
}

// ── Come si legge una riga ────────────────────────────────────────────────────

/**
 * Chi ha scritto. **Quattro esiti, non tre**, e il quarto e' quello che tiene onesti gli altri:
 *
 *  · `mio`        — `senderId === me.user.id`;
 *  · `avversario` — e' fra i partecipanti della stanza e non sono io. ⚠️ In un incontro a SQUADRE
 *                   sarebbe anche un compagno: la schermata non scrive mai la parola, mostra il
 *                   NOME, quindi l'etichetta interna non mente a nessuno;
 *  · `staff`      — la stanza e' nota e l'autore NON e' fra i partecipanti: puo' scrivere solo chi
 *                   e' membro di una parte o passa dalla porta amministrativa (`inviaMessaggio`),
 *                   quindi chi resta fuori dall'elenco e' lo staff;
 *  · 🔴 `ignoto`  — **non si puo' dire.** La stanza non e' arrivata (e allora «non e' fra i
 *                   partecipanti» non prova niente), oppure non sappiamo chi sono io (e allora un
 *                   partecipante potrebbe essere me), oppure il messaggio non porta un autore.
 *                   Chiamarlo `staff` o `avversario` sarebbe un'attribuzione tirata a sorte.
 */
export type AutoreChat = 'mio' | 'avversario' | 'staff' | 'ignoto';

/** Cosa dire del TESTO oltre al testo stesso. `null` = niente, il testo parla da solo. */
export type NotaChat = 'eliminato' | 'moderato' | 'senza-testo' | null;

/** La copy delle note e degli autori che non sono un nome: accanto al criterio, come `LIMULO_NOME_COPY`. */
export const NOTA_CHAT_COPY: Readonly<Record<Exclude<NotaChat, null>, string>> = Object.freeze({
  eliminato: 'matches.chat.note.deleted',
  moderato: 'matches.chat.note.moderated',
  'senza-testo': 'matches.chat.note.noText',
});

export const AUTORE_CHAT_COPY = Object.freeze({
  mio: 'matches.chat.author.me',
  staff: 'matches.chat.author.staff',
  ignoto: 'matches.chat.author.unknown',
});

export interface RigaChat {
  /** La chiave della riga: l'id del messaggio, o la posizione se il server non ne ha dato uno. */
  key: string;
  autore: AutoreChat;
  nome: string;
  /** Vero quando `nome` e' una NOSTRA frase e non un nome scelto da qualcuno (v. `participantLabelOf`). */
  nomeSmorzato: boolean;
  /** Il testo da mostrare; `''` quando non ce n'e' (eliminato, solo allegati). */
  testo: string;
  nota: NotaChat;
  /** L'istante in ms dall'UNICO parser di date della sezione (`normalizeInstant`), o `null`. */
  ora: number | null;
}

/**
 * Le righe della chat, una per messaggio, nell'ordine in cui il server le manda (ascendente dentro
 * la pagina, `listaMessaggi`). ⚠️ Non si riordina: un ordinamento nostro sulle date sarebbe una
 * seconda regola accanto a quella del server, e con un `createdAt` mancante non saprebbe dove
 * mettere la riga.
 *
 * @param meId         `me.user.id` di `GET /v1/me`, o `null` se non e' arrivato.
 * @param participants i partecipanti della stanza (`GET …/chat`), o `null` se non e' arrivata.
 * @param t            la traduzione, come parametro: questo livello resta puro.
 */
export function righeDellaChat(
  items: readonly LimuloChatMessage[],
  meId: string | null,
  participants: readonly LimuloChatParticipant[] | null,
  t: (key: string) => string,
): RigaChat[] {
  const io = meId?.trim() || null;
  const perId = new Map((participants ?? []).map((p) => [p.userId, p]));
  return items.map((m, i) => {
    const partecipante = m.senderId === '' ? undefined : perId.get(m.senderId);
    const autore: AutoreChat =
      m.senderId === ''
        ? 'ignoto'
        : io !== null && m.senderId === io
          ? 'mio'
          : participants === null
            ? 'ignoto'
            : partecipante === undefined
              ? 'staff'
              : io === null
                ? 'ignoto'
                : 'avversario';
    /**
     * Il nome. ⚠️ Un `ignoto` che e' nella stanza porta il SUO nome: che sia un partecipante e'
     * vero, e' solo il lato (io o l'altro) a non essere noto — e un nome vero non attribuisce il lato.
     */
    const etichetta =
      autore === 'mio'
        ? { testo: t(AUTORE_CHAT_COPY.mio), smorzato: false }
        : autore === 'staff'
          ? { testo: t(AUTORE_CHAT_COPY.staff), smorzato: false }
          : partecipante !== undefined
            ? participantLabelOf(partecipante, t)
            : { testo: t(AUTORE_CHAT_COPY.ignoto), smorzato: true };
    // 🔴 `deleted` vince sul corpo anche se il corpo arrivasse pieno: il motore lo svuota, e un
    // messaggio eliminato che torna leggibile per un difetto altrui non lo rimettiamo noi a schermo.
    const eliminato = m.status === 'deleted';
    // Un corpo di soli spazi non e' un testo: a schermo sarebbe una bolla vuota senza spiegazione.
    const testo = eliminato || m.body.trim() === '' ? '' : m.body;
    const nota: NotaChat = eliminato
      ? 'eliminato'
      : m.status === 'moderated'
        ? 'moderato'
        : testo.trim() === ''
          ? 'senza-testo'
          : null;
    return {
      key: m.id || `riga-${i}`,
      autore,
      nome: etichetta.testo,
      nomeSmorzato: etichetta.smorzato,
      testo,
      nota,
      ora: normalizeInstant(m.createdAt),
    };
  });
}

/**
 * Quanti messaggi ci sono PRIMA di quelli mostrati, o `null` se non ce ne sono (o non si sa).
 * `total` conta anche eliminati e moderati, ma anche le righe mostrate li contano: la differenza
 * sono i messaggi piu' vecchi, che questa schermata non va a prendere (niente paginazione
 * all'indietro, per ora) — e lo deve DIRE, o la conversazione sembra cominciare a meta'.
 */
export function messaggiPrecedenti(total: number | null, mostrati: number): number | null {
  if (total === null) return null;
  const nascosti = total - mostrati;
  return nascosti > 0 ? nascosti : null;
}

// ── Cosa si puo' mandare ─────────────────────────────────────────────────────

export type StatoBozza = 'vuota' | 'pronta' | 'troppo-lunga';

export interface BozzaChat {
  /** Il corpo che parte: il testo col `trim`, cioe' esattamente cio' che il motore misura. */
  corpo: string;
  lunghezza: number;
  stato: StatoBozza;
  /** Il contatore si mostra? Vicino al tetto, o oltre. */
  contatore: boolean;
}

/**
 * La bozza nel campo. ⚠️ Il tetto si misura sul testo TRIMMATO, come il motore: 2001 caratteri di
 * cui uno e' un a-capo finale sono 2000, e bloccarli sarebbe un rifiuto che il server non fa.
 * ⛔ Niente `maxLength` sul campo al posto di questa funzione: un incollato oltre il tetto verrebbe
 * TAGLIATO in silenzio, e un messaggio mutilato senza dirlo e' peggio di un bottone spento col
 * motivo accanto.
 */
export function bozzaChat(testo: string): BozzaChat {
  const corpo = testo.trim();
  const lunghezza = corpo.length;
  return {
    corpo,
    lunghezza,
    stato: lunghezza === 0 ? 'vuota' : lunghezza > LIMULO_CHAT_MAX_CORPO ? 'troppo-lunga' : 'pronta',
    contatore: lunghezza >= LIMULO_CHAT_SOGLIA_CONTATORE,
  };
}

// ── Com'e' andato l'invio ────────────────────────────────────────────────────

/**
 * L'esito di un invio. **Cinque, e il quarto e' la ragione per cui non sono due.**
 *
 *  · `inviato`     — 201;
 *  · `non-ammesso` — 403 senza indizi (`forbidden`): e' `chat.non-partecipante`, l'unico 403 che
 *                    l'invio dichiara (`contratto.js:197`). ⚠️ Il MOTIVO non attraversa il bordo
 *                    (`buildProviderRefusalError` porta solo lo stato), quindi la frase dice il
 *                    rifiuto e la regola, non una causa che non ci e' stata detta;
 *  · `rifiutato`   — 400/422: corpo vuoto o oltre il tetto (`chat.corpo-vuoto`,
 *                    `chat.corpo-troppo-lungo`). Il campo li impedisce entrambi, quindi arrivarci
 *                    vuol dire che il tetto del server non e' piu' il nostro;
 *  · 🔴 `incerto`  — **non sappiamo se e' stato scritto.** Rete caduta, nostra scadenza, 5xx, e il
 *                    424: `inviaMessaggio` scrive e POI rilegge (`core/chat.js:257-279`), e
 *                    un'eccezione dopo la scrittura esce come 424 col messaggio gia' salvato. ⇒ si
 *                    RILEGGE la lista, e la copy manda a guardarla prima di rimandare: un doppione
 *                    in chat e' fastidioso, un «non inviato» falso fa ripetere a vuoto;
 *  · `non-inviato` — ogni altro rifiuto: e' nato PRIMA del motore (sessione, app, organizzazione,
 *                    incontro inesistente), quindi niente e' stato scritto.
 *                    ⚠️ Anche l'interruttore dell'organizzazione spento (`switch-off`,
 *                    `E_QLASH_ENDPOINT_DISABLED`, p.es. `enableChat`): il BFF lo controlla in
 *                    `createRuntimeContext`, prima della capability. ⛔ NON e' `non-ammesso`: fino
 *                    al 2026-09-26 lo era, e la frase diceva «possono scriverci solo i giocatori
 *                    dell'incontro e lo staff» a un giocatore dell'incontro. Ora dice «non inviato»
 *                    + il titolo neutro della sua famiglia («funzione non attiva su questo ambiente»).
 */
export type EsitoInvioChat =
  | { tipo: 'inviato' }
  | { tipo: 'non-ammesso'; failure: LimuloFailure }
  | { tipo: 'rifiutato'; failure: LimuloFailure }
  | { tipo: 'incerto'; failure: LimuloFailure }
  | { tipo: 'non-inviato'; failure: LimuloFailure };

const VERDETTI_INCERTI = new Set<LimuloFailure['verdict']>([
  'network',
  'timeout',
  'server',
  'internal-or-switch-off',
  'domain-or-missing',
  'unknown',
]);

/** L'esito dall'errore di RTK Query (`{status, data}`), o `inviato` se non c'e' errore. */
export function esitoInvioChat(error: unknown): EsitoInvioChat {
  if (error === undefined || error === null) return { tipo: 'inviato' };
  const failure = limuloFailure(error);
  if (failure.verdict === 'forbidden') return { tipo: 'non-ammesso', failure };
  if (failure.verdict === 'unprocessable' || failure.verdict === 'bad-request') {
    return { tipo: 'rifiutato', failure };
  }
  if (VERDETTI_INCERTI.has(failure.verdict)) return { tipo: 'incerto', failure };
  return { tipo: 'non-inviato', failure };
}
